import { useId } from 'react';
import {
  Badge,
  Button,
  Card,
  CardTitle,
  DataTable,
  type DataTableColumn,
} from '@wundo/ui';
import { UserPicker } from '@/components/pickers/UserPicker.tsx';
import type { UserOption } from '@/components/pickers/userOption.ts';
import {
  activeRunOf,
  type MaintenanceRun,
  type MaintenanceTask,
} from '@/lib/maintenance.ts';

const STATUS_TONE = {
  pending: 'info',
  running: 'info',
  finished: 'success',
  failed: 'danger',
  cancelled: 'neutral',
} as const;

/** "12 / 340 users" — the total is only known once the first batch has run. */
function progressOf(run: MaintenanceRun): string {
  return run.users_total === null
    ? 'starting…'
    : `${run.users_done} / ${run.users_total} users`;
}

function countsOf(run: MaintenanceRun): string[] {
  return Object.entries(run.report).map(([key, value]) => `${key}: ${value}`);
}

function when(run: MaintenanceRun): string {
  return new Date(run.finished_at ?? run.started_at ?? run.created_at).toLocaleString(
    'nl-BE',
  );
}

const runColumns: DataTableColumn<MaintenanceRun>[] = [
  {
    key: 'when',
    header: 'When',
    render: (run) => <span className='whitespace-nowrap'>{when(run)}</span>,
  },
  {
    key: 'status',
    header: 'Status',
    render: (run) => <Badge tone={STATUS_TONE[run.status]}>{run.status}</Badge>,
  },
  { key: 'started_by_email', header: 'By', render: (run) => run.started_by_email ?? '—' },
  {
    key: 'result',
    header: 'Result',
    render: (run) => {
      if (run.error) return <span className='text-danger'>{run.error}</span>;
      const counts = countsOf(run);
      return counts.length > 0 ? counts.join(' · ') : '—';
    },
  },
];

export interface MaintenanceTaskCardProps {
  task: MaintenanceTask;
  /** Which user this task is narrowed to, if any. */
  user: UserOption | null;
  onUserChange: (user: UserOption | null) => void;
  /** One task runs at a time, so every card knows whether the console is busy. */
  busy: boolean;
  running: boolean;
  onRun: () => void;
  onCancel: (run: MaintenanceRun) => void;
}

/**
 * One maintenance task: what it does, who may narrow it, and what it has done.
 *
 * The history is the point of the card rather than a detail of it — these
 * tasks rewrite user data in bulk, so the question is always "has this already
 * been run here, and what did it touch".
 */
export function MaintenanceTaskCard({
  task,
  user,
  onUserChange,
  busy,
  running,
  onRun,
  onCancel,
}: MaintenanceTaskCardProps) {
  const fieldId = useId();
  const active = activeRunOf(task);

  return (
    <Card as='section' className='flex flex-col gap-4'>
      <div className='flex items-start justify-between gap-4'>
        <div>
          <CardTitle>{task.title}</CardTitle>
          <p className='mt-1 text-sm text-ink-muted'>{task.description}</p>
        </div>
        <Button
          className='shrink-0'
          variant='accent'
          disabled={active !== null || busy}
          loading={running}
          onClick={onRun}
        >
          Run
        </Button>
      </div>

      <div className='flex max-w-120 flex-col gap-1.5'>
        <label htmlFor={fieldId} className='text-sm font-bold text-ink'>
          Limit to one user (optional)
        </label>
        <UserPicker
          id={fieldId}
          value={user}
          onChange={onUserChange}
          disabled={active !== null}
        />
      </div>

      {active !== null && (
        <div className='flex items-center justify-between gap-4 rounded-md bg-fill px-3.5 py-2.5 text-sm text-ink'>
          <span>Running — {progressOf(active)}</span>
          <Button variant='quiet' size='sm' onClick={() => onCancel(active)}>
            Cancel
          </Button>
        </div>
      )}

      {task.runs.length > 0 ? (
        <DataTable<MaintenanceRun>
          label={`${task.title} runs`}
          columns={runColumns}
          rows={task.runs}
          rowKey={(run) => run.id}
        />
      ) : (
        <p className='text-sm text-ink-muted'>Never run here.</p>
      )}
    </Card>
  );
}
